import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://www.qiyoucinema.com/about#about',
  'url': 'https://www.qiyoucinema.com/about',
  'name': '关于奇优影院 - 品牌介绍与联系方式',
  'description': '奇优影院是专注于提供高清电影电视剧免费在线观看的平台，以秒开无卡顿的极速播放体验为核心，致力于为用户打造最优质的在线观影服务。',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://www.qiyoucinema.com/#organization',
  'name': '奇优影院',
  'url': 'https://www.qiyoucinema.com/',
  'description': '奇优影院 - 高清电影电视剧免费在线观看平台，提供秒开无卡顿的流畅播放体验',
  'foundingDate': '2018',
  'contactPoint': {
    '@type': 'ContactPoint',
    'contactType': 'customer service',
    'email': 'support@qiyoucinema.com',
    'availableLanguage': ['Chinese'],
  },
};

export default function AboutPage() {
  const teamMembers = [
    {
      name: '张明远',
      role: '创始人 & CEO',
      description: '10年互联网产品经验，致力于用技术改变观影体验',
    },
    {
      name: '李雨桐',
      role: '技术总监',
      description: '前阿里云技术专家，专注视频编解码与P2P加速技术',
    },
    {
      name: '王晓峰',
      role: '产品总监',
      description: '深耕视频领域8年，打造极致用户体验',
    },
  ];

  const milestones = [
    { year: '2018', title: '奇优影院正式上线', desc: '以"不缓冲、秒开播放"为核心定位，正式面向用户提供服务' },
    { year: '2019', title: '用户突破100万', desc: '凭借流畅的播放体验，获得用户广泛认可，日活用户快速增长' },
    { year: '2020', title: '引入4K超清技术', desc: '全面升级视频编码技术，支持4K HDR超高清画质播放' },
    { year: '2021', title: '片库突破5万部', desc: '与多家内容提供商达成合作，影视资源库大幅扩充' },
    { year: '2023', title: '用户突破3000万', desc: '成为国内领先的免费在线观影平台之一' },
    { year: '2025', title: 'AI智能推荐上线', desc: '基于用户观影偏好，智能推荐个性化内容，提升观影体验' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar alwaysShowBg />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />

      <main className="pt-16">
        <div className="w-full px-4 md:px-8 py-4 md:py-6 bg-background-100 border-b border-background-300/20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-100 mt-2">
              关于奇优影院
            </h1>
          </div>
        </div>

        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 mb-4">
                品牌故事
              </h2>
              <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-3xl mx-auto">
                奇优影院创立于2018年，是一支由资深互联网人和视频技术专家组成的团队。
                我们深知等待缓冲是观影最大的痛点，因此从一开始就将"秒开无卡顿"作为产品的核心追求。
                通过自主研发的P2P-CDN混合加速技术和智能码率调节算法，
                奇优影院为用户提供不缓冲、流畅播放的极致观影体验。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mb-12 md:mb-16">
              {[
                { icon: 'ri-eye-line', title: '我们的愿景', text: '让每一个人都能随时随地享受高清流畅的观影体验，不受任何限制' },
                { icon: 'ri-rocket-line', title: '我们的使命', text: '用技术打破播放壁垒，提供秒开无卡顿的极速播放服务' },
                { icon: 'ri-heart-line', title: '我们的价值观', text: '用户至上，技术驱动，内容为王，体验为先' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-lg bg-background-100 border border-background-300/20 text-center"
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10 text-primary-500">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-medium text-foreground-200 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 text-center mb-8">
                发展历程
              </h2>
              <div className="space-y-0">
                {milestones.map((m, idx) => (
                  <div key={m.year} className="flex gap-4 md:gap-6">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-primary-500 flex-shrink-0 mt-1.5"></div>
                      {idx < milestones.length - 1 && (
                        <div className="w-0.5 flex-1 bg-background-300/50 my-1"></div>
                      )}
                    </div>
                    <div className="pb-8">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-heading text-primary-500">{m.year}</span>
                        <h3 className="text-base font-medium text-foreground-200">{m.title}</h3>
                      </div>
                      <p className="text-sm text-foreground-500 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 text-center mb-8">
                核心团队
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                {teamMembers.map((member) => (
                  <div
                    key={member.name}
                    className="p-6 rounded-lg bg-background-100 border border-background-300/20 text-center"
                  >
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                      <i className="ri-user-line text-2xl text-foreground-500"></i>
                    </div>
                    <h3 className="text-base font-medium text-foreground-200 mb-1">
                      {member.name}
                    </h3>
                    <p className="text-xs text-primary-400 mb-2">{member.role}</p>
                    <p className="text-sm text-foreground-500 leading-relaxed">
                      {member.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 text-center mb-4">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                来自真实用户的使用评价
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: '电影发烧友小陈', rating: 5, text: '用了奇优影院之后就没换过别的平台，真的秒开不卡顿，画质也很清晰。晚上高峰期看4K电影都流畅得很，强烈推荐！' },
                  { name: '追剧达人小王', rating: 5, text: '以前用别的平台经常缓冲，气到想砸手机。换了奇优影院后简直重生，追剧从未如此顺畅。片库也很全，新剧更新快！' },
                  { name: '影视爱好者老李', rating: 5, text: '没有广告这点最让我满意。很多免费平台广告多到烦，奇优影院真正做到了纯净观影。技术实力确实过硬，播放流畅度没话说。' },
                ].map((review) => (
                  <div
                    key={review.name}
                    className="p-5 rounded-lg bg-background-100 border border-background-300/20"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed mb-3">
                      "{review.text}"
                    </p>
                    <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-100 text-center mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                如有任何问题或建议，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-2xl mx-auto mb-8">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-300/20">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-200 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">support@qiyoucinema.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-300/20">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-200 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-500">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-300/20">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-200 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-500">奇优影院官方</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-300/20">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-200 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周日 9:00 - 21:00</p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-base font-medium whitespace-nowrap transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-line text-lg"></i>
                  </span>
                  返回首页开始观影
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}